import { SxProps, Theme } from '@mui/material';
import { AlertBanner } from '@resurs-internal/mui';
import { ReactNode } from 'react';

type Props = {
    title: string;
    description?: string;
    sx?: SxProps<Theme>;
};

const LINK_PATTERN = /^(.*?)<a\s+href="([^"]*)">([^<]*)<\/a>(.*)$/s;

// Some translated descriptions embed a single <a href="..."> link (e.g. "...back to
// resursbank.dk"). AlertBanner's `subtitle` is typed as `string`, but its implementation
// renders it as a plain React child rather than via dangerouslySetInnerHTML, so passing a
// ReactNode here (cast below) renders a real, validated link instead of escaped text. Any
// description that doesn't match this exact pattern, or whose href isn't http(s), is
// rendered as plain text -- never build a link from an unvalidated href.
const parseDescription = (description: string): ReactNode => {
    const match = description.match(LINK_PATTERN);
    if (!match) {
        return description;
    }

    const [, before, href, linkText, after] = match;
    if (!/^https?:\/\//i.test(href)) {
        return description;
    }

    return (
        <>
            {before}
            <a href={href}>{linkText}</a>
            {after}
        </>
    );
};

const ErrorMessage = ({ title, description, sx }: Props) => {
    const subtitle = description ? parseDescription(description) : undefined;

    return (
        <AlertBanner
            severity="error"
            title={title}
            // See parseDescription above -- this cast is intentional and safe given
            // AlertBanner's current implementation. Do not widen AlertBannerProps.subtitle
            // to "fix" this; keep the cast contained to this one call site.
            subtitle={subtitle as unknown as string}
            // Space consecutive error banners apart (e.g. submit failure + consent fetch failure).
            sx={[{ '& + &': { marginTop: 2 } }, ...(Array.isArray(sx) ? sx : [sx])]}
        />
    );
};

export default ErrorMessage;
